/**
 * App Mode Configuration
 *
 * Controls which features/routes are available based on the deployment environment.
 * Set via NEXT_PUBLIC_APP_MODE env variable.
 *
 * Modes:
 *   - development:   All routes open, Base Sepolia
 *   - pre-launch:    Landing page only, all other routes blocked
 *   - post-launch:   Full site minus /play, users go to featured coin
 *   - production:    Full production site, all routes open
 */

export type AppMode = 'development' | 'pre-launch' | 'launch-ready' | 'post-launch' | 'production';

export const VALID_MODES: AppMode[] = ['development', 'pre-launch', 'launch-ready', 'post-launch', 'production'];

/** Cookie name used for preview mode override */
export const MODE_OVERRIDE_COOKIE = '__app_mode_override';

/** Get the base app mode from the env variable */
export function getAppMode(): AppMode {
  const raw = process.env.NEXT_PUBLIC_APP_MODE || 'development';
  if (VALID_MODES.includes(raw as AppMode)) {
    return raw as AppMode;
  }
  console.warn(`[app-mode] Invalid NEXT_PUBLIC_APP_MODE="${raw}", falling back to "development"`);
  return 'development';
}

/** Feature flags derived from the active app mode */
export interface AppModeConfig {
  mode: AppMode;
  /** Whether /play (discover grids) is accessible */
  showPlayPage: boolean;
  /** Whether /leaderboard is accessible */
  showLeaderboard: boolean;
  /** Whether the in-app portfolio (ProfileModal opened from the bottom nav / wallet dropdown) is accessible */
  showPortfolio: boolean;
  /** Whether the boardroom link (protocol overview / keeper buybacks) is accessible */
  showBoardroom: boolean;
  /** Whether the site is in landing-only mode (pre-launch) */
  isLandingOnly: boolean;
  /** Whether the site is in launch-ready mode (pre-launch + countdown, auto-transitions when pool is indexed) */
  isLaunchReady: boolean;
}

/** Derive feature flags from an app mode */
export function getAppModeConfig(mode?: AppMode): AppModeConfig {
  const resolvedMode = mode ?? getAppMode();

  switch (resolvedMode) {
    case 'pre-launch':
      return {
        mode: resolvedMode,
        showPlayPage: false,
        showLeaderboard: false,
        showPortfolio: false,
        showBoardroom: false,
        isLandingOnly: true,
        isLaunchReady: false,
      };

    case 'launch-ready':
      return {
        mode: resolvedMode,
        showPlayPage: false,
        showLeaderboard: false,
        showPortfolio: false,
        showBoardroom: false,
        isLandingOnly: true,
        isLaunchReady: true,
      };

    case 'post-launch':
      return {
        mode: resolvedMode,
        showPlayPage: false,
        showLeaderboard: true,
        showPortfolio: true,
        showBoardroom: true,
        isLandingOnly: false,
        isLaunchReady: false,
      };

    case 'production':
      return {
        mode: resolvedMode,
        showPlayPage: true,
        showLeaderboard: true,
        showPortfolio: true,
        showBoardroom: true,
        isLandingOnly: false,
        isLaunchReady: false,
      };

    case 'development':
    default:
      return {
        mode: resolvedMode,
        showPlayPage: true,
        showLeaderboard: true,
        showPortfolio: true,
        showBoardroom: true,
        isLandingOnly: false,
        isLaunchReady: false,
      };
  }
}

/**
 * Client-side hook helper: read the override cookie from document.cookie.
 * Returns the overridden mode or null if no override is set.
 */
export function getOverrideModeFromCookie(): AppMode | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${MODE_OVERRIDE_COOKIE}=([^;]*)`));
  if (!match) return null;
  const val = decodeURIComponent(match[1]);
  if (VALID_MODES.includes(val as AppMode)) return val as AppMode;
  return null;
}

/**
 * Get the effective app mode, considering cookie override.
 * For client-side use.
 *
 * WARNING: Do not call this at render time in components — it reads
 * document.cookie which differs between server (null) and client,
 * causing hydration mismatches. Use `useAppModeConfig()` instead.
 */
export function getEffectiveAppMode(): AppMode {
  const override = getOverrideModeFromCookie();
  return override ?? getAppMode();
}
